<script>
	import thumb_u2ILNHzT8FM from '$lib/Components/Youtube/images/u2ILNHzT8FM.jpg?w=610';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import acousticWindowInsertsImg from './images/acoustic-window-inserts.jpg';
	import caulkingExteriorHistoricHomePaintImg from './images/caulking-exterior-historic-home-paint.png';
	import comparePriceWindowSoundproofingImg from './images/compare-price-window-soundproofing.jpg';
	import decorativeSoundAbsorbingWallPanelsImg from './images/decorative-sound-absorbing-wall-panels.jpg';
	import reflectionAbsorptionDiffusionImg from './images/reflection-absorption-diffusion.jpg';
	import resourcesSoundproofingHealth from './images/resources-soundproofing-health.jpg';
	import retrofoamImg from './images/retrofoam.png';

	// images
</script>

<Section>
	<p>
		The most effective soundproofing materials depends on where you are trying
		to soundproof and what noise you are trying to soundproof. How do you use
		the space and how does noise impact your use of it? There are different
		processes for <a href="/resources/soundproof-room-music"
			>how to soundproof a room for music</a
		>
		vs
		<a href="/resources/blog/soundproof-home-office"
			>how to soundproof a home office</a
		>.
	</p>
	<Image
		src={resourcesSoundproofingHealth}
		alt="woman inside house with noise symbols showing two types of soundproofing"
	/>
</Section>

<Section>
	<h2>The Two Types of Soundproofing</h2>
	<p>
		First, a disclaimer: you can't completely soundproof a space as long as
		there is air. Anywhere air goes, sound goes too because sound travels via
		air.
	</p>
	<p>There are two types of soundproofing:</p>
	<p>
		<strong>Sound reduction </strong>, which stops sound from entering a space.
		Sound reduction will help keep your space free outside noises such as
		weather, outdoor conversations, and traffic.
	</p>
	<p>
		<strong>Sound absorption</strong>, which stops sound already inside a space
		from echoing and reverberating.
	</p>
	<Image
		src={reflectionAbsorptionDiffusionImg}
		alt="explanation of noise reflection, absorption, and diffusion"
	/>
	<Bubble>
		{#snippet titleSlot()}
			<h3>Use these two types of soundproofing to:</h3>
		{/snippet}
		<ul>
			<li>
				<strong
					>Identify the weakest areas where sound will enter and treat those</strong
				>. Sound waves, like water, will travel the easiest path, so plug those
				up first.
			</li>
			<li>
				<strong>Isolate problem areas</strong>. Sometimes it's easier to treat
				the source before the sound waves reach the space you want to
				soundproof.
			</li>
		</ul>
	</Bubble>
</Section>

<Section>
	<h2>Best Soundproofing Material</h2>
	<p>
		Neighbors, barking dogs, cars – stop it before it reaches your ears. Adding
		these soundproofing materials or <a
			href="/resources/blog/most-effective-soundproofing-materials"
			>soundproofing products</a
		> to your home or office will reduce the sound that breaks through easy entry
		points like windows, doors, and even walls.
	</p>
	<Image
		src={retrofoamImg}
		alt="open wall with retrofoam added as insulation and effective soundproofing material"
	/>
	<h3>Soundproofing Spray Foam</h3>
	<p>
		<strong>Definition</strong>: Foam sprayed from a can that can be added as
		insulation to walls for insulation.
	</p>
	<p><strong>Best for</strong>: Existing walls, floors (between levels)</p>
	<h3>Mass Loaded Vinyl Sound Barrier</h3>
	<p>
		<strong>Definition</strong>: Heavy, versatile material that reduces noise
		wherever it is placed.
	</p>
	<p>
		<strong>Best for</strong>: Hollow doors, between walls, floors, and
		ceilings, around pipes.
	</p>
	<Image
		src={caulkingExteriorHistoricHomePaintImg}
		alt="person with mask adding acoustic caulk to siding for soundproofing"
	/>
	<h3>Acoustic Caulk</h3>
	<p>
		<strong>Definition</strong>: Like regular caulk, acoustic caulk seals gaps
		in windows, walls, and doors and blocks air. Acoustic caulk has added noise
		blocking properties to stop sound vibrations.
	</p>
	<p>
		<strong>Best for</strong>: Cracks in window frames, doors, walls, ceilings.
	</p>
	<Image
		src={acousticWindowInsertsImg}
		alt="person with mask adding acoustic caulk to siding for soundproofing"
	/>
	<h3>Acoustic Window Inserts</h3>
	<p>
		<strong>Definition</strong>:
		<a href="/products/acoustic-window-inserts">Soundproof window inserts</a>
		that fit onto existing windows to air seal. Made from high-grade acrylic to both
		reduce and absorb sound.
	</p>
	<p>
		<strong>Best for</strong>: Single-pane windows, historic windows. They work
		on double-pane windows, but you will get less noise reduction.
	</p>
	<!-- <Button href="/instant-estimate">TRY OUR INSERT PRICING CALCULATOR</Button> -->
	<Image
		src={decorativeSoundAbsorbingWallPanelsImg}
		alt="decorative sound absorbing wall panels installed next to stairs"
	/>
	<p>
		<strong>Definition</strong>: Decorative sound absorbing wall panels (yes,
		decorative, why should you settle for plain?) absorb noise within a space in
		an aesthetically pleasing way. They come prefabricated or available to
		customize with your own art.
	</p>
	<p>
		<strong>Best for</strong>: Rooms where noise echoes – large, flat surfaces
		like walls with no windows.
	</p>
	<h3>Soundproof Underlayment</h3>
	<p>
		<strong>Definition</strong>: Soundproof underlayment is a rubber mat that
		you can place underneath other flooring materials to decrease sound
		transmission between levels. It can also eliminate noise from footsteps.
	</p>
	<p>Best for: Creaking floors, upper levels.</p>
	<h3>Acoustic Floor Tiles</h3>
	<p>
		<strong>Definition</strong>: Acoustic floor tiles absorb sound during impact
		and from nearby sources.
	</p>
	<p><strong>Best for</strong>: Echoing room.</p>
</Section>

<Section>
	<h2>Comparing Most Effective Soundproofing Materials</h2>
	<p>
		Once you know where you are adding the soundproofing materials and what
		products you want, how can you compare which brand is the best? The best
		soundproofing material manufacturers will back up their product with a phone
		call discussing which types of noise it blocks or absorbs.
	</p>
	<p>
		Many products also list their STC rating. STC, or Sound Transmission Class
		Rating, is a system that rates how well an assembly of building materials
		reduces or absorbs sound. The higher a product's STC rating, the better it
		is at soundproofing. See this <a href="/resources/stc-rating"
			>STC rating chart</a
		> for relative noise levels. It's a great way to compare products across brands
		and types.
	</p>
	<p>
		The prices below are average for that type of window and may include
		installation costs. For example, the double-pane window cost is taken from
		<a
			href="https://www.remodeling.hw.net/cost-vs-value/window-replacement-vinyl-upscale?y=2020"
			target="_blank"
			rel="noreferrer">Remodeling</a
		> and is based on a 3×5 window. We tried to find windows that would have comparative
		STC ratings.
	</p>
	<Image src={comparePriceWindowSoundproofingImg} />
</Section>

<Section>
	<h2>Watch Effective Soundproofing Materials in Action</h2>
	<p>
		Visit our <a
			href="https://www.youtube.com/c/Indowwindows"
			target="_blank"
			rel="noreferrer">YouTube channel</a
		> for more tips on the best soundproofing material and how to most effectively
		block noise.
	</p>
	<Youtube
		posterImageSrc={thumb_u2ILNHzT8FM}
		src="https://www.youtube.com/embed/u2ILNHzT8FM"
		title="Soundproofing Windows with Indow Inserts"
	/>
</Section>

<GetAFreeEstimate title="Let Us Help You" backgroundColor="blue" />
<Reviews />
